import React, { useEffect, useState } from 'react';
import { Col, Row, Button, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
registerLocale('es', es);

const getLocalDateISO = (d) => {
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

import useToken from 'hooks/useToken';
import AkasiaPosStatsCards from './components/AkasiaPosStatsCards';
import AkasiaPosUserStatsCards from './components/AkasiaPosUserStatsCards';
import AkasiaPosActivityChart from './components/AkasiaPosActivityChart';
import AkasiaPosUserActivityChart from './components/AkasiaPosUserActivityChart';
import AkasiaPosUsers from './components/AkasiaPosUsers';
import AkasiaPosLicenses from './components/AkasiaPosLicenses';
import AkasiaPosVentasRenovaciones from './components/AkasiaPosVentasRenovaciones';

const AkasiaPosDashboard = () => {
    const { token } = useToken();

    const [dailyActivity, setDailyActivity] = useState([]);
    const [userActivity, setUserActivity] = useState([]);

    // UI State
    const [loadingLicenses, setLoadingLicenses] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [error, setError] = useState(null);

    // Filter & Modal State
    const [userTypeFilter, setUserTypeFilter] = useState('All');
    const [showUserModal, setShowUserModal] = useState(false);
    const [userDetailData, setUserDetailData] = useState([]);
    const [modalDate, setModalDate] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    // License Date Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Init Dates on Mount
    useEffect(() => {
        const today = new Date();
        const endStr = getLocalDateISO(today);
        const start = new Date(today);
        start.setDate(today.getDate() - 30);
        const startStr = getLocalDateISO(start);
        setStartDate(startStr);
        setEndDate(endStr);
    }, []);

    // Fetch ONLY User Stats (Independent of License Date Filter)
    const fetchUserData = async () => {
        setLoadingUsers(true);
        try {
            // Default User View is Last 30 Days fixed
            const today = new Date();
            const endStr = getLocalDateISO(today);
            const start = new Date(today);
            start.setDate(today.getDate() - 30);
            const startStr = getLocalDateISO(start);

            const body = JSON.stringify({
                daysBack: 30,
                startDate: startStr,
                endDate: endStr,
                userType: userTypeFilter
            });

            const res = await fetch('/api/AkasiaPosDashboard/GetUserActivity', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body
            });

            if (res.ok) {
                const data = await res.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const parsed = JSON.parse(data.detalles);
                    // table2 contains activity data (table1 is response status)
                    if (parsed.table2) setUserActivity(parsed.table2);
                }
            }
        } catch (err) {
            console.error("Fetch User Stats error:", err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchLicenseData = async () => {
        setLoadingLicenses(true);
        try {
            const body = JSON.stringify({
                startDate: startDate,
                endDate: endDate
            });

            const res = await fetch('/api/AkasiaPosDashboard/GetLicenseActivity', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body
            });

            if (res.ok) {
                const data = await res.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const parsed = JSON.parse(data.detalles);
                    if (parsed.table2) setDailyActivity(parsed.table2);
                }
            } else if (res.status === 401) {
                location.href = '';
            }
        } catch (err) {
            console.error("Fetch License error:", err);
            setError("Failed to load license stats");
        } finally {
            setLoadingLicenses(false);
        }
    };


    // Effect: Fetch Users when Token or UserType changes
    useEffect(() => {
        if (token) fetchUserData();
    }, [token, userTypeFilter]);

    // Effect: Fetch Licenses when Token or Dates change
    useEffect(() => {
        if (token && startDate && endDate) fetchLicenseData();
    }, [token, startDate, endDate]);

    const handleGlobalDrillDown = (dateStr) => {
        if (dateStr && (dateStr.length === 10 || dateStr.match(/^\d{4}-\d{2}-\d{2}$/))) {
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) {
                const iso = d.toISOString().split('T')[0];
                setStartDate(iso);
                setEndDate(iso);
            }
        }
    };

    useEffect(() => {
        if (showUserModal && modalDate && token) {
            const fetchDetail = async () => {
                setModalLoading(true);
                try {
                    const res = await fetch('/api/AkasiaPosDashboard/GetUserActivity', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            daysBack: 0,
                            startDate: modalDate,
                            endDate: modalDate,
                            userType: userTypeFilter
                        })
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data.iResponseType === 1 && data.detalles) {
                            const parsed = JSON.parse(data.detalles);
                            // table2 contains activity data (hourly when single day)
                            if (parsed.table2) setUserDetailData(parsed.table2);
                            else setUserDetailData([]);
                        }
                    }
                } catch (e) {
                    console.error("Modal fetch error", e);
                } finally {
                    setModalLoading(false);
                }
            };
            fetchDetail();
        }
    }, [showUserModal, modalDate, userTypeFilter, token]);

    // User Chart Drill Down (Open Modal)
    const handleUserDrillDown = (dateStr) => {
        if (!dateStr) return;
        let iso = dateStr;
        if (dateStr.length !== 10) {
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) iso = d.toISOString().split('T')[0];
        }
        setModalDate(iso);
        setShowUserModal(true);
        // Data fetch handled by useEffect
    };





    return (
        <div>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
                <h4 className="mb-0 text-primary fw-bold">
                    Akasia POS
                    <span className="text-info fw-medium"> — Dashboard</span>
                </h4>
            </div>

            {/* License Stats Section */}
            <h6 className="text-uppercase text-muted fw-bold mb-2 small ls-1">Estadísticas de Licencias</h6>
            <AkasiaPosStatsCards />

            <Row className="g-3 mb-4 mt-2">
                <Col lg={12}>
                    <AkasiaPosActivityChart
                        activityData={dailyActivity}
                        title="Actividad de Licencias"
                        loading={loadingLicenses}
                        onDateClick={handleGlobalDrillDown}
                        filterElement={
                            <div className="d-flex gap-2 align-items-center">
                                <span className="text-muted small fw-bold">Filtro:</span>
                                <DatePicker
                                    selected={startDate ? new Date(startDate + 'T00:00:00') : null}
                                    onChange={(date) => setStartDate(date ? getLocalDateISO(date) : '')}
                                    className="form-control form-control-sm"
                                    dateFormat="dd 'de' MMMM yyyy"
                                    locale="es"
                                    placeholderText="Fecha Inicio"
                                />
                                <span className="text-muted">-</span>
                                <DatePicker
                                    selected={endDate ? new Date(endDate + 'T00:00:00') : null}
                                    onChange={(date) => setEndDate(date ? getLocalDateISO(date) : '')}
                                    className="form-control form-control-sm"
                                    dateFormat="dd 'de' MMMM yyyy"
                                    locale="es"
                                    placeholderText="Fecha Fin"
                                />
                            </div>
                        }
                    />
                </Col>
            </Row>


            <AkasiaPosLicenses />
            
            {/* Sales & Renewals Section */}
            <h6 className="text-uppercase text-muted fw-bold mb-2 mt-4 small ls-1">Estadísticas de Ventas y Renovaciones</h6>
            <AkasiaPosVentasRenovaciones />


            {/* User Stats Section */}
            <h6 className="text-uppercase text-muted fw-bold mb-2 mt-4 small ls-1">Estadísticas de Usuarios</h6>
            <AkasiaPosUserStatsCards />

            <Row className="g-3 mb-4 mt-2">
                <Col lg={12}>
                    {!showUserModal ? (
                        <AkasiaPosUserActivityChart
                            activityData={userActivity}
                            title={`Actividad de Usuarios`}
                            loading={loadingUsers}
                            error={error}
                            onDateClick={handleUserDrillDown}
                            filterElement={
                                <div className="d-flex gap-2 align-items-center" onClick={e => e.stopPropagation()}>
                                    <DatePicker
                                        selected={null}
                                        onChange={(date) => {
                                            if (date) {
                                                const iso = date.toISOString().split('T')[0];
                                                handleUserDrillDown(iso);
                                            }
                                        }}
                                        className="form-control form-control-sm"
                                        style={{ width: '150px' }}
                                        dateFormat="dd 'de' MMMM yyyy"
                                        locale="es"
                                        placeholderText="Ver detalle por día"
                                    />
                                    <Form.Select
                                        size="sm"
                                        style={{ width: '150px' }}
                                        value={userTypeFilter}
                                        onChange={(e) => setUserTypeFilter(e.target.value)}
                                    >
                                        <option value="All">Todos</option>
                                        <option value="LOGIN">Inicio de Sesión</option>
                                        <option value="POS">Ingreso a POS</option>
                                    </Form.Select>
                                </div>
                            }
                        />
                    ) : (
                        <div className="position-relative">
                            {modalLoading && (
                                <div className="position-absolute top-50 start-50 translate-middle py-5" style={{ zIndex: 10 }}>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                            <AkasiaPosUserActivityChart
                                activityData={userDetailData}
                                title={`Últimas Conexiones por Hora (${modalDate ? new Date(modalDate + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : ''})`}
                                error={null}
                                onDateClick={null}
                                filterElement={
                                    <div className="d-flex gap-2 align-items-center" onClick={e => e.stopPropagation()}>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setShowUserModal(false)}
                                        >
                                            <i className="bi bi-arrow-left me-1"></i> Volver
                                        </Button>
                                        <div className="mx-2" style={{ borderLeft: '1px solid #ccc', height: '20px' }}></div>
                                        <DatePicker
                                            selected={modalDate ? new Date(modalDate + 'T00:00:00') : null}
                                            onChange={(date) => {
                                                if (date) {
                                                    const iso = date.toISOString().split('T')[0];
                                                    handleUserDrillDown(iso);
                                                }
                                            }}
                                            className="form-control form-control-sm"
                                            style={{ width: '150px' }}
                                            dateFormat="dd 'de' MMMM yyyy"
                                            locale="es"
                                            placeholderText="Cambiar fecha"
                                        />
                                        <Form.Select
                                            size="sm"
                                            style={{ width: '150px' }}
                                            value={userTypeFilter}
                                            onChange={(e) => setUserTypeFilter(e.target.value)}
                                        >
                                            <option value="All">Todos</option>
                                            <option value="LOGIN">Inicio de Sesión</option>
                                            <option value="POS">Ingreso a POS</option>
                                        </Form.Select>
                                    </div>
                                }
                            />
                        </div>
                    )}
                </Col>
            </Row>




            <AkasiaPosUsers />

        </div>
    );
};

export default AkasiaPosDashboard;

